import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  useTheme,
  Button,
  CircularProgress,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Images } from "../../utils/Images";
import AuthenticationTextInput from "../../components/common/TextInput/AuthenticationTextInput";
import { DonateButtonVariant } from "../../components/common/Button/Button";
import DonateLeftBanner from "../../components/common/DonateLeftBanner";
import { styled } from "@mui/material/styles";
import BannerSection from "../../components/common/BannerSection";
import CurrencySelectorPopup from "../../components/common/CurrencySelectorPopup"; // Import the popup
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { handleDonate } from "../../utils/Donation";
import { useSelector, useDispatch } from "react-redux";
import { validateDonationAmount } from "../../utils/donationValidation";
import {
  sanitizeFormFieldValue,
  validateIndianMobile,
} from "../../utils/phoneValidation";
import { fetchCmsHomeAction } from "../../store/slices/cmsSlice";


const CustomDonateButton = styled(Button)(({ theme, isMobile, isTablet }) => ({
  backgroundColor: "#F04951",
  color: "#FFFFFF",
  borderRadius: "10px",
  boxShadow: "0px 4px 0px #141414",
  // padding: isMobile ? "12px 16px" : isTablet ? "14px 20px" : "15px 24px",
  width: "100%", // 100% width for responsiveness
  height: isMobile ? "44px" : isTablet ? "48px" : "52px",
  fontFamily: "Red Hat Display, sans-serif",
  fontWeight: 700,
  fontSize: isMobile ? "13px" : isTablet ? "14px" : "15px",
  lineHeight: isMobile ? "13px" : isTablet ? "14px" : "15px",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: isMobile ? "6px" : isTablet ? "7px" : "8px",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#d63f46",
    boxShadow: "0px 4px 0px #141414",
  },
  "&:active": {
    boxShadow: "0px 2px 0px #141414",
    transform: "translateY(2px)",
  },
  "&:disabled": {
    backgroundColor: "#d63f46",
    opacity: 0.7,
    cursor: "not-allowed",
  },
}));
// Support options will be generated dynamically based on amount
const getSupportOptions = (amount, currencySymbol = "₹") => {
  if (!amount || Number(amount) <= 0) {
    return [
      { label: `12% - ${currencySymbol}0`, value: "12" },
      { label: `15% - ${currencySymbol}0`, value: "15" },
      { label: `20% - ${currencySymbol}0`, value: "20" },
    ];
  }
  const donationAmount = Number(amount);
  return [
    { label: `12% - ${currencySymbol}${Math.round(donationAmount * 0.12)}`, value: "12" },
    { label: `15% - ${currencySymbol}${Math.round(donationAmount * 0.15)}`, value: "15" },
    { label: `20% - ${currencySymbol}${Math.round(donationAmount * 0.20)}`, value: "20" },
  ];
};

const currencyList = [
  { code: "INR", symbol: "₹" },
  { code: "USD", symbol: "$" },
  { code: "AED", symbol: "د.إ" },
  { code: "CAD", symbol: "C$" },
  { code: "AUD", symbol: "A$" },
  { code: "GBP", symbol: "£" },
];

const DonateWithProductPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const cmsHome = useSelector((state) => state.cms.home);
  
  // Get campaign info and base amount from navigation state
  const campaignTitleFromState = location.state?.campaignTitle || "";
  const campaignIdFromState = location.state?.campaignId;
  const baseAmountFromState = location.state?.baseAmount || location.state?.amount || 1000;
  const currencyFromState = location.state?.currency || currencyList[0];
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch CMS data if not available
  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);
  
  const handleDonateClick = async () => {
    // Validate form
    const validationErrors = {};
    if (!form.name) validationErrors.name = "Full Name is required";
    if (!form.email) validationErrors.email = "Email is required";
    const phoneCheck = validateIndianMobile(form.phone);
    if (!phoneCheck.isValid) validationErrors.phone = phoneCheck.error;
    if (!form.postal) validationErrors.postal = "Postal Code is required";
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      alert("Please fill all required fields");
      return;
    }
    
    // Validate minimum donation amount (check if overseas based on currency)
    const isOverseas = currency.code !== "INR";
    const validation = validateDonationAmount(totalAmount, cmsHome, isOverseas, currency.symbol);
    if (!validation.isValid) {
      setAmountError(validation.error);
      return;
    }
    
    // Clear error if validation passes
    setAmountError("");

    if (loading) return;

    setLoading(true);
    try {
      const additionalData = {
        currency: currency.code,
        postalCode: form.postal,
        whatsappUpdates: whatsapp,
        campaignId: campaignIdFromState || "",
        campaignName: campaignTitleFromState || "",
        baseAmount: baseAmount,
        productTotal: productTotal,
        tip: tip,
        selectedProducts: selectedProducts, // Include product details
        anonymousCheck: anonymousCheck ? 1 : 0
      };

      await handleDonate(
        form,
        Number(totalAmount),
        navigate,
        additionalData,
        user?.id,
        null // No snackbar for now
      );
    } catch (error) {
      console.error("Donation error:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const theme = useTheme();

  // Custom media query for screens 900px to 1200px
  const isMediumScreen = useMediaQuery(
    "(min-width: 900px) and (max-width: 1200px)"
  );
  // Custom media query for screens below 900px
  const isSmallScreen = useMediaQuery("(max-width: 899px)");
  // Custom media query for mobile screens (600px and below)
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");
  const mobileView = isMobile;
  
  // DonateRightForm state and handlers
  const [supportBy, setSupportBy] = useState("12");
  const [baseAmount, setBaseAmount] = useState(baseAmountFromState);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showCurrencyPopup, setShowCurrencyPopup] = useState(false);
  const [currency, setCurrency] = useState(currencyFromState);
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Load products from sessionStorage on mount
  useEffect(() => {
    try {
      const products = JSON.parse(sessionStorage.getItem("selectedProducts") || "[]");
      setSelectedProducts(products);
      
      // Also get base amount from sessionStorage if available
      const storedBaseAmount = sessionStorage.getItem("baseDonationAmount");
      if (storedBaseAmount) {
        setBaseAmount(Number(storedBaseAmount));
      }
    } catch (error) {
      console.error("Error loading products:", error);
    }
  }, []);
  
  // Calculate product total
  const productTotal = selectedProducts.reduce((sum, product) => sum + (product.totalAmount || 0), 0);
  
  // Calculate donation amount (base + products) for tip calculation
  const donationAmount = baseAmount + productTotal;
  
  // Get dynamic support options based on donation amount
  const supportOptions = getSupportOptions(donationAmount, currency.symbol);
  
  // Calculate tip based on donation amount and selected percentage
  const tipPercentage = Number(supportBy) || 12;
  const tip = Math.round(donationAmount * (tipPercentage / 100));
  const tipPercent = tipPercentage;
  
  // Calculate total amount (donation + tip)
  const totalAmount = donationAmount + tip;
  
  // Clear amount error when total amount changes
  useEffect(() => {
    if (amountError) {
      setAmountError("");
    }
  }, [totalAmount]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    postal: "",
  });
  const [whatsapp, setWhatsapp] = useState(true);
  const [anonymousCheck, setAnonymousCheck] = useState(false);
  const [amountError, setAmountError] = useState(""); // Validation error for amount

  // Auto-fill form when user is logged in
  useEffect(() => {
    if (user && user.id) {
      // Only auto-fill if fields are empty and user has the data
      setForm((prevForm) => ({
        ...prevForm,
        name: prevForm.name || user.name || "",
        email: prevForm.email || user.email || "",
        phone: prevForm.phone || user.phone || user.mobile || "",
        // postal code is not available in user profile, so leave it empty
      }));
    }
  }, [user]);

  const handleInputChange = (field) => (e) => {
    const value = sanitizeFormFieldValue(field, e.target.value);
    setForm({ ...form, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  // Currency dropdown handlers
  const handleCurrencyClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCurrencySelect = (cur) => {
    setCurrency(cur);
    setAnchorEl(null);
  };
  
  const handleCurrencyPopupSelect = (cur) => {
    setCurrency(cur);
  };

  const handleCloseCurrencyPopup = () => {
    setShowCurrencyPopup(false);
  };
  return (
    <Box>
      <Box
        sx={{
          mb: "40px",
          mt: "30px",
          display: isSmallScreen ? "block" : "none",
        }}
      >
        <BannerSection title="Donate with Products" subtitle={false} />
      </Box>

      <Box
        sx={{
          background: isMobile ? "unset" : "rgba(232, 232, 232, 0.40)",
          // minHeight: "100vh",
          p: isMobile ? "0" : isMediumScreen ? "16px" : "30px",
          width: "94.4%",
          maxWidth: "1440px",
          borderRadius: "20px",
          mx: "auto",
        }}
      >
        <Box sx={{ display: "flex", gap: isSmallScreen ? "0" : "24px" }}>
          {/* DonateRightForm content */}
          {/* DonateLeftBanner */}
          <Box>
            <DonateLeftBanner />
          </Box>
          <Box
            sx={{
              background: "#fff",
              borderRadius: isMobile ? "20px" : "20px",
              boxShadow: isMobile ? "none" : "0 4px 24px rgba(0,0,0,0.08)",
              p: isMobile ? "0px" : "30px 26px",
              pb: isSmallScreen ? "80px" : undefined, // breathing room for fixed donate bar on mobile
              minHeight: isMobile ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              flex: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: isMobile
                    ? "12px"
                    : isMediumScreen
                    ? "14px"
                    : "20px",
                  fontWeight: 700,
                  color: "#1D1D1B",
                  mb: isMobile ? 1 : "20px",
                  lineHeight: isMobile ? "20px" : 1.2,
                  letterSpacing: "-0.4px",
                }}
              >
                {campaignTitleFromState || "This Janmashtami, Help Abandoned & Helpless Widows Have Healthy Meal To Survive"}
              </Typography>
            </Box>
            {/* Products List */}
            {selectedProducts.length > 0 && (
              <Box
                sx={{
                  background: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  border: "0.5px dashed #B9B9B8",
                  p: "18px 18px 0 18px",
                  mb: 3,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "18px",
                }}
              >
                {selectedProducts.map((item, idx) => (
                  <Box
                    key={item.id || idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      pb: "18px",
                      borderBottom:
                        idx === selectedProducts.length - 1
                          ? "none"
                          : "0.8px solid #97979623",
                    }}
                  >
                    {/* Product Image */}
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: "8px",
                        background: "rgba(249, 238, 198, 0.46)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        mr: "16px",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={item.image}
                        alt={item.title}
                        width="32"
                        height="32"
                        style={{ objectFit: "contain" }}
                      />
                    </Box>
                    {/* Product Details */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: "#1D1D1B",
                          fontSize: "14px",
                          mb: "10px",
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#686866",
                          fontWeight: 500,
                        }}
                      >
                        Qty: {item.quantity}
                      </Typography>
                    </Box>
                    {/* Product Price */}
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "14px",
                          color: "#1D1D1B",
                        }}
                      >
                        ₹{item.totalAmount}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            {/* Support us by */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#E8E8E8",
                padding: "10px 10px 10px 20px",
                borderRadius: isMobile ? "4px" : "10px",
                mb: "22px",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? "12px" : isSmallScreen ? "14px" : "16px",
                  color: "#141414",
                }}
              >
                Support us by :
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box>
                  <select
                    value={supportBy}
                    onChange={(e) => setSupportBy(e.target.value)}
                    style={{
                      minWidth: isMobile ? "110px" : "160px",
                      background: "#fff",
                      borderRadius: "8px",
                      fontWeight: 600,
                      fontSize: isMobile ? "10px" : "15px",
                      border: "1.114px solid #B9B9B8",
                      height: isMobile ? "32px" : "40px",
                    }}
                  >
                    {supportOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </Box>
              </Box>
            </Box>
            {/* Green Box - Base Amount, Tip Amount, Total Amount */}
            <Box
              sx={{
                border: "1.114px  solid #BDD188",
                background: "#F7F9EC",
                borderRadius: "8px",
                px: "18px",
                py: "13px",
                mb: "18px",
                width: "100%",
                fontFamily: "inherit",
              }}
            >
              {/* Base Amount */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: "18px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: isMobile ? "11px" : "14px",
                    color: "#1D1D1B",
                    fontWeight: 600,
                  }}
                >
                  Base Amount
                </Typography>
                <Typography
                  sx={{
                    fontSize: isMobile ? "13px" : "14px",
                    color: "#8EA946",
                    fontWeight: 700,
                  }}
                >
                  {currency.symbol}
                  {baseAmount.toLocaleString()}
                </Typography>
              </Box>
              <Box
                sx={{
                  borderBottom: "1px dashed #B9B9B8",
                  mb: "18px",
                }}
              />
              {/* Tip Amount */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: "18px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: isMobile ? "11px" : "14px",
                    color: "#1D1D1B",
                    fontWeight: 600,
                  }}
                >
                  Tip Amount
                </Typography>
                <Typography
                  sx={{
                    fontSize: isMobile ? "13px" : "14px",
                    color: "#8EA946",
                    fontWeight: 700,
                  }}
                >
                  {currency.symbol}
                  {tip}{" "}
                  <span
                    style={{
                      color: "#1D1D1B",
                      fontWeight: 600,
                      fontSize: isMobile ? "11px" : "14px",
                    }}
                  >
                    ({tipPercent}%)
                  </span>
                </Typography>
              </Box>
              <Box
                sx={{
                  borderBottom: "1px dashed #B9B9B8",
                  mb: "18px",
                }}
              />
              {/* Total Amount */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#1D1D1B",
                    fontSize: isMobile ? "12px" : "14px",
                  }}
                >
                  Total Amount
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#1D1D1B",
                    fontSize: isMobile ? "13px" : "14px",
                  }}
                >
                  {currency.symbol}
                  {totalAmount.toLocaleString()}
                </Typography>
              </Box>
            </Box>
            {/* Validation error text */}
            {amountError && (
              <Typography
                sx={{
                  color: "#F04951",
                  fontSize: isMobile ? "11px" : "12px",
                  mt: -1,
                  mb: 2,
                  ml: 1,
                }}
              >
                {amountError}
              </Typography>
            )}
            {/* Form fields */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isMobile ? "auto" : "auto auto",
                columnGap: "27px",
              }}
            >
              <Box>
                <AuthenticationTextInput
                  label="Full Name"
                  value={form.name}
                  onChange={handleInputChange("name")}
                  placeholder="Enter Full Name"
                  required
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Box>
              <Box>
                <AuthenticationTextInput
                  label="Email ID"
                  value={form.email}
                  onChange={handleInputChange("email")}
                  placeholder="Enter E mail Id"
                  required
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Box>
              <Box>
                <AuthenticationTextInput
                  label="Phone Number"
                  value={form.phone}
                  onChange={handleInputChange("phone")}
                  placeholder="Enter Phone Number"
                  phoneInput
                  required
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
              </Box>
              <Box>
                <AuthenticationTextInput
                  label="Postal Code"
                  value={form.postal}
                  onChange={handleInputChange("postal")}
                  placeholder="Enter Postal Code"
                  required
                  error={!!errors.postal}
                  helperText={errors.postal}
                />
              </Box>
            </Box>
            {/* Anonymous Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={anonymousCheck}
                  onChange={(e) => setAnonymousCheck(e.target.checked)}
                  sx={{
                    color: "#f04951",
                    "&.Mui-checked": { color: "#f04951" },
                    p: 0,
                    mr: "10px",
                    width: isMobile ? "16px" : "20px",
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: isMobile ? "12px" : "14px",
                    fontWeight: 500,
                    color: "#1D1D1B",
                  }}
                >
                  Make my donation anonymous
                </Typography>
              }
              sx={{ mb: isMobile ? 1 : "14px", mx: "0" }}
            />
            {/* WhatsApp Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.checked)}
                  sx={{
                    color: "#f04951",
                    "&.Mui-checked": { color: "#f04951" },
                    p: 0,
                    mr: "10px",
                    width: isMobile ? "16px" : "20px",
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: isMobile ? "12px" : "14px",
                    fontWeight: 500,
                    color: "#1D1D1B",
                  }}
                >
                  I want to receive transaction and donation updates on WhatsApp
                </Typography>
              }
              sx={{ mb: isMobile ? 1 : "14px", mx: "0" }}
            />
            {/* Terms */}
            <Typography
              sx={{
                fontSize: isMobile ? "10px" : "14px",
                color: "#1D1D1B",
                mb: isMobile ? 2 : 3,
                fontWeight: 500,
              }}
            >
              By continuing, you are agreeing to{" "}
              <Box
                component={Link}
                to="/terms-conditions"
                sx={{ 
                  color: "#FF5858", 
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:active": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:visited": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:focus": {
                    color: "#FF5858",
                    textDecoration: "none"
                  }
                }}
              >
                Terms of Use
              </Box>{" "}
              and{" "}
              <Box
                component={Link}
                to="/privacy-policy"
                sx={{ 
                  color: "#FF5858", 
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:active": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:visited": {
                    color: "#FF5858",
                    textDecoration: "none"
                  },
                  "&:focus": {
                    color: "#FF5858",
                    textDecoration: "none"
                  }
                }}
              >
                Privacy Policy
              </Box>
            </Typography>
            {/* Donate Button */}
            <div
              style={{
                width: "100%",
                display: isSmallScreen ? "none" : "block",
              }}
            >
              <CustomDonateButton 
                onClick={handleDonateClick} 
                isMobile={isMobile}
                disabled={loading}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
                    Processing...
                  </Box>
                ) : (
                  `Donate ${currency.symbol}${totalAmount.toLocaleString()}`
                )}
              </CustomDonateButton>
            </div>
          </Box>
        </Box>
      </Box>
        <div
        style={{
          width: "100%",
          position: "fixed",
          backgroundColor: "#fff",
          bottom: 0,
          left: 0,
          padding: "16px 10px",
          boxShadow: " 0 -4px 16px 0 rgba(0, 0, 0, 0.10)",
          display: isSmallScreen ? "block" : "none",
        }}
      >
        <CustomDonateButton  
          onClick={handleDonateClick} 
          isMobile={isMobile}
          disabled={loading}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
              Processing...
            </Box>
          ) : (
            `Donate ${currency.symbol}${totalAmount.toLocaleString()}`
          )}
        </CustomDonateButton>
      </div>
       {/* Add Currency Selector Popup */}
      <CurrencySelectorPopup
        open={showCurrencyPopup}
        onClose={handleCloseCurrencyPopup}
        selectedCurrency={currency}
        onCurrencySelect={handleCurrencyPopupSelect}
      />
    </Box>
  );
};

export default DonateWithProductPage;
